// Semi-logical AI controller for bot players. One `BotAI` instance per
// bot. Same pattern as `NpcManager` / `ChestManager` — transport-neutral
// state, no server/client imports.
//
// The AI is a priority tree with short reaction delays and occasional
// suboptimal picks. Not a full behavior tree, not pathfinding — just
// enough to feel alive.

import type { Role } from '../net/protocol';
import { rollChoices, type Rarity } from './powerups';
import { VENUES } from './bosses';

export const BOT_TARGET_PER_TEAM = 2;

export type BotSelf = {
  id: string;
  role: Role;
  x: number;
  z: number;
  yaw: number;
  hp: number;
  hpMax: number;
  level: number;
  alive: boolean;
  questMask: number;
  /** 0..1 charge toward ultimate. Fires when >= 1 AND wantUlt. */
  ultCharge: number;
  /** Server-set — true while leap arc / UI bubble is live. Bot won't
   *  re-fire while active. */
  ultActive: boolean;
};

export type BotOpponent = {
  id: string;
  role: Role;
  x: number;
  z: number;
  hood: boolean;
  alive: boolean;
  hp: number;
};

export type BotNpc = {
  x: number;
  z: number;
  stage: 0 | 1 | 2 | 3;
  shiny: boolean;
};

export type BotBoss = {
  side: Role;
  x: number;
  z: number;
  alive: boolean;
  hp: number;
};

export type BotContext = {
  nowMs: number;
  elapsedMatchSec: number;
  opponents: ReadonlyArray<BotOpponent>;
  allies: ReadonlyArray<BotOpponent>;
  npcs: ReadonlyArray<BotNpc>;
  bosses: ReadonlyArray<BotBoss>;
};

export type BotDecision = {
  dx: number;
  dz: number;
  yaw: number;
  wantHood: boolean;
  /** Bot wants to fire its ultimate this tick. Virgin = Ultra Instinct
   *  (instant), Chad = Photo Bomb leap to ultTargetX/Z. Server still
   *  validates charge + alive + not-already-active. */
  wantUlt: boolean;
  /** Only read when role === 'ext' and wantUlt is true. Ignored for
   *  Virgin. Clamped server-side to LEAP_MIN..MAX range. */
  ultTargetX?: number;
  ultTargetZ?: number;
};

const BOT_TUNING = {
  ENGAGE_SIGHT: 16,
  FARM_SEARCH: 14,
  RING_IDEAL: 3.5,
  // Chad circle is 3u radius; the Virgin ring's dead-zone starts
  // at 4u. Parking inside the dead-zone (<4u) keeps the bot safe from
  // the ring while still inside its own circle-attack range. 0.6 leaves
  // headroom against `atTarget` radius 1.0 — bot lands at 0.5..1.6u
  // from the foe, always inside the dead zone, always in circle range.
  CIRCLE_IDEAL: 0.6,
  HOOD_HP_THRESHOLD: 0.55,
  HOOD_COOLDOWN_MS: 8000,
  // Ultimate triggers. Virgin fires when an enemy is close-ish and we
  // need a combat edge; Chad fires when an enemy cluster is in leap
  // range. Cooldown throttles the "want" flag so the bot doesn't spam
  // it every tick the instant charge refills.
  ULT_COOLDOWN_MS: 6000,
  ULT_VIRGIN_ENGAGE_RANGE: 10, // closest enemy within 10u → pop Ultra Instinct
  ULT_CHAD_MIN_RANGE: 3,       // don't leap onto feet
  ULT_CHAD_MAX_RANGE: 11,      // must be within the leap reach
  FACTION_BASE_VIRGIN:   { x: -85, z: 0 },
  FACTION_BASE_CHAD: { x:  85, z: 0 },
  MAP_BOUNDS: { xMin: -100, xMax: 100, zMin: -50, zMax: 50 },
  TARGET_REFRESH_MS: 1200,
} as const;

// Per-bot personality. Each archetype pulls different levers on the
// same decision tree so a room of 6 bots feels like 6 players with
// different play styles, not 6 copies of one bot. Every personality
// keeps the bot in constant motion — we dropped the dither freeze
// because idle pauses were feeding AFK-farm exploits.
//
// Archetypes:
//  • hunter      — always looking for a fight, rarely retreats
//  • farmer      — camps NPCs, avoids enemies until levelled up
//  • reluctant   — walks venues + NPCs, bails early at low HP
//  • kamikaze    — no retreat, dives inside enemy range every time
//  • pro         — balanced, rarely blunders, engages smartly
//  • opportunist — whatever's closest, flexible priorities
export type BotPersonality =
  | 'hunter'
  | 'farmer'
  | 'reluctant'
  | 'kamikaze'
  | 'pro'
  | 'opportunist';

type TargetKind = 'enemy' | 'boss' | 'npc' | 'venue';

type PersonalityTuning = {
  // HP fraction below which the bot retreats to base. 0 disables
  // retreat entirely (kamikaze fights to the last HP).
  retreatPct: number;
  // Probability the bot picks the runner-up target from its priority
  // list instead of the optimal one. Cheap "mistake" simulation.
  blunderChance: number;
  // Multiplier on the role-specific engage distance. <1 hugs closer,
  // >1 stays further out.
  engageIdealMult: number;
  // Ordered preference for which target kinds to consider. First
  // available option that exists in context gets picked (subject to
  // the blunder roll).
  priority: ReadonlyArray<TargetKind>;
};

const PERSONALITY_TUNING: Record<BotPersonality, PersonalityTuning> = {
  hunter:      { retreatPct: 0.18, blunderChance: 0.10, engageIdealMult: 0.85, priority: ['enemy', 'boss', 'npc', 'venue'] },
  farmer:      { retreatPct: 0.40, blunderChance: 0.20, engageIdealMult: 1.00, priority: ['npc', 'venue', 'enemy', 'boss'] },
  reluctant:   { retreatPct: 0.50, blunderChance: 0.25, engageIdealMult: 1.20, priority: ['venue', 'npc', 'enemy', 'boss'] },
  kamikaze:    { retreatPct: 0.00, blunderChance: 0.12, engageIdealMult: 0.55, priority: ['enemy', 'boss', 'npc', 'venue'] },
  pro:         { retreatPct: 0.25, blunderChance: 0.05, engageIdealMult: 0.90, priority: ['enemy', 'boss', 'npc', 'venue'] },
  opportunist: { retreatPct: 0.32, blunderChance: 0.18, engageIdealMult: 1.00, priority: ['npc', 'enemy', 'venue', 'boss'] },
};

const ALL_PERSONALITIES: ReadonlyArray<BotPersonality> = [
  'hunter', 'farmer', 'reluctant', 'kamikaze', 'pro', 'opportunist',
];

export function randomBotPersonality(): BotPersonality {
  return ALL_PERSONALITIES[Math.floor(Math.random() * ALL_PERSONALITIES.length)];
}

// How often a bot rerolls its archetype, in ms. Clockwork intervals
// feel predictable ("oh the hunter becomes a farmer at exactly 0:45")
// so we sample a fresh random window every time — 20..55 s. The mean
// (~37 s) is long enough for a personality to matter, short enough
// that a single match will usually see each bot shift at least once.
const PERSONALITY_REROLL_MIN_MS = 20_000;
const PERSONALITY_REROLL_MAX_MS = 55_000;

function pickRerollDelayMs(): number {
  return PERSONALITY_REROLL_MIN_MS
    + Math.random() * (PERSONALITY_REROLL_MAX_MS - PERSONALITY_REROLL_MIN_MS);
}

export class BotAI {
  private targetX = 0;
  private targetZ = 0;
  private nextRefreshMs = 0;
  private lastHoodMs = 0;
  private lastUltMs = 0;
  // Anti-stuck tracking. Mirror of the NPC stuck detector: if the
  // bot barely moved over ~900 ms while it had a live target, it's
  // pinned against a wall/prop and needs a kicked-perpendicular
  // detour.
  private lastStuckCheckMs = 0;
  private lastStuckX = 0;
  private lastStuckZ = 0;
  // True when the current target was set by an enemy engagement. Kept
  // as a flag so tests + debug tools can introspect it; the dither
  // freeze it used to guard is gone, so every bot now moves on every
  // tick regardless of archetype (constant-motion rule).
  private engagingEnemy = false;
  // Per-bot archetype. Rerolled on a *random* interval (not a fixed
  // clock) so bots don't all flip at the same instant. The initial
  // value is set in the constructor; think() triggers subsequent
  // rerolls when `now >= nextPersonalityRerollMs`.
  personality: BotPersonality;
  private nextPersonalityRerollMs = 0;

  constructor(personality?: BotPersonality) {
    this.personality = personality ?? randomBotPersonality();
    // Schedule the first reroll against wall-clock now; think() will
    // re-sync against its own `ctx.nowMs` on the first call.
    this.nextPersonalityRerollMs = Date.now() + pickRerollDelayMs();
  }

  // Force a new archetype right now + reschedule the next reroll.
  // Exposed so debug tools / tests can exercise the reroll path
  // without waiting the full 20..55 s window.
  rerollPersonality(nowMs = Date.now()): BotPersonality {
    this.personality = randomBotPersonality();
    this.nextPersonalityRerollMs = nowMs + pickRerollDelayMs();
    return this.personality;
  }

  think(_dt: number, self: BotSelf, ctx: BotContext): BotDecision {
    const now = ctx.nowMs;

    if (!self.alive) {
      return { dx: 0, dz: 0, yaw: self.yaw, wantHood: false, wantUlt: false };
    }

    // Personality drift — random-interval reroll. Happens out-of-band
    // from target refresh so the bot doesn't also re-pick a target on
    // the same tick (feels more natural when behavior shifts between
    // two goal picks, not simultaneously with them).
    if (now >= this.nextPersonalityRerollMs) {
      this.personality = randomBotPersonality();
      this.nextPersonalityRerollMs = now + pickRerollDelayMs();
    }

    if (now >= this.nextRefreshMs || this.atTarget(self)) {
      this.pickTarget(self, ctx);
      this.nextRefreshMs = now + BOT_TUNING.TARGET_REFRESH_MS;
    }

    // Stuck detection — if the bot has barely moved over the last
    // STUCK_WINDOW_MS while it DOES have a target that isn't at its
    // feet, it's pinned against a wall/prop. Re-pick a fresh target
    // on the next think so it tries another angle. Threshold is
    // generous (1.2 world units) because the bot's walk speed at 15
    // Hz think means ~0.3 u/tick, so over 900 ms the honest-moving
    // bot should cover well past 1.2 u.
    if (now - this.lastStuckCheckMs >= 900) {
      const targetDx = this.targetX - self.x;
      const targetDz = this.targetZ - self.z;
      const targetDist = Math.hypot(targetDx, targetDz);
      const moved = Math.hypot(self.x - this.lastStuckX, self.z - this.lastStuckZ);
      if (targetDist > 1.5 && moved < 1.2) {
        // Apply a random perpendicular offset to the current target so
        // the next think routes around whatever's blocking us. Ignored
        // on target refresh tick since pickTarget already picks fresh.
        const angle = Math.atan2(targetDz, targetDx) + (Math.random() < 0.5 ? Math.PI / 2 : -Math.PI / 2);
        const kick = 4 + Math.random() * 4;
        this.targetX = clamp(this.targetX + Math.cos(angle) * kick, BOT_TUNING.MAP_BOUNDS.xMin, BOT_TUNING.MAP_BOUNDS.xMax);
        this.targetZ = clamp(this.targetZ + Math.sin(angle) * kick, BOT_TUNING.MAP_BOUNDS.zMin, BOT_TUNING.MAP_BOUNDS.zMax);
        // Force an earlier-than-normal full retarget if this keeps
        // failing — sometimes the personality's only option is
        // genuinely walled off.
        this.nextRefreshMs = Math.min(this.nextRefreshMs, now + 300);
      }
      this.lastStuckCheckMs = now;
      this.lastStuckX = self.x;
      this.lastStuckZ = self.z;
    }

    const dx = this.targetX - self.x;
    const dz = this.targetZ - self.z;
    const dist = Math.hypot(dx, dz);
    const yaw = dist > 0.01 ? Math.atan2(dx, -dz) : self.yaw;

    const hpPct = self.hp / self.hpMax;
    const hoodReady = (now - this.lastHoodMs) > BOT_TUNING.HOOD_COOLDOWN_MS;
    // dt dropped to a fixed tick duration to keep hood-roll fair at
    // 15 Hz regardless of the think-call cadence.
    const HOOD_ROLL_PER_SEC = 0.9;
    // Both factions now use the H-slot. Virgin → hood (survival via
    // invisibility on low HP). Chad → mogging (sustain via lifesteal,
    // trigger when actively engaging). Different trigger heuristics
    // since the effects reward different contexts.
    let wantHood = false;
    if (hoodReady && Math.random() < HOOD_ROLL_PER_SEC / 15) {
      if (self.role === 'int' && hpPct < BOT_TUNING.HOOD_HP_THRESHOLD) wantHood = true;
      // Chad mogs when actively engaging an enemy + not at full HP.
      if (self.role === 'ext' && this.engagingEnemy && hpPct < 0.9) wantHood = true;
    }
    if (wantHood) this.lastHoodMs = now;

    // Ultimate — fire when charged, off-cooldown, and conditions fit
    // the role's ult kind. Virgin pops Ultra Instinct defensively OR
    // offensively (any enemy within 10u is reason enough). Chad looks
    // for the closest enemy inside leap range and commits a Photo
    // Bomb at them. The server still validates charge + alive etc.
    let wantUlt = false;
    let ultTargetX: number | undefined;
    let ultTargetZ: number | undefined;
    const ultReady = self.ultCharge >= 1 && !self.ultActive && (now - this.lastUltMs) > BOT_TUNING.ULT_COOLDOWN_MS;
    if (ultReady) {
      const nearestEnemy = nearestAlive(ctx.opponents, self.x, self.z, 40);
      if (nearestEnemy) {
        const edx = nearestEnemy.x - self.x;
        const edz = nearestEnemy.z - self.z;
        const edist = Math.hypot(edx, edz);
        if (self.role === 'int') {
          // Ultra Instinct: fire when an enemy is within engage range.
          // No target coords needed — it's an AoE slow bubble.
          if (edist <= BOT_TUNING.ULT_VIRGIN_ENGAGE_RANGE) {
            wantUlt = true;
          }
        } else {
          // Chad leap: must land within leap range, not onto the foe's
          // feet. Aim slightly short of the target so the AoE lands on
          // top of them.
          if (edist >= BOT_TUNING.ULT_CHAD_MIN_RANGE && edist <= BOT_TUNING.ULT_CHAD_MAX_RANGE) {
            wantUlt = true;
            ultTargetX = nearestEnemy.x;
            ultTargetZ = nearestEnemy.z;
          }
        }
      }
    }
    if (wantUlt) this.lastUltMs = now;

    if (dist < 0.25) {
      return { dx: 0, dz: 0, yaw, wantHood, wantUlt, ultTargetX, ultTargetZ };
    }
    return { dx: dx / dist, dz: dz / dist, yaw, wantHood, wantUlt, ultTargetX, ultTargetZ };
  }

  // Cheap predicate — true if the next think() call will actually
  // consult ctx.opponents / ctx.allies / ctx.npcs / ctx.bosses.
  // Lets the server skip building those arrays when the bot is just
  // advancing along its existing target vector.
  needsContext(self: BotSelf, nowMs: number): boolean {
    if (!self.alive) return false;
    return nowMs >= this.nextRefreshMs || this.atTarget(self);
  }

  // Call on match reset so the bot forgets its old goal.
  reset(): void {
    this.targetX = 0;
    this.targetZ = 0;
    this.nextRefreshMs = 0;
    this.lastHoodMs = 0;
    this.lastUltMs = 0;
    this.lastStuckCheckMs = 0;
    this.lastStuckX = 0;
    this.lastStuckZ = 0;
    this.engagingEnemy = false;
    // Fresh match → fresh archetype + fresh reroll window.
    this.personality = randomBotPersonality();
    this.nextPersonalityRerollMs = Date.now() + pickRerollDelayMs();
  }

  private atTarget(self: BotSelf): boolean {
    const dx = this.targetX - self.x;
    const dz = this.targetZ - self.z;
    return dx * dx + dz * dz < 1.0;
  }

  // Priority tree:
  //  0. HP low → retreat to own base (skipped for kamikaze archetype)
  //  1..N: iterate personality.priority in order, pushing whichever
  //        candidates actually exist this tick.
  //  last: wander — always present so the bot is never idle.
  // `blunderChance` (personality-specific) swaps optimal→runner-up.
  private pickTarget(self: BotSelf, ctx: BotContext): void {
    const tuning = PERSONALITY_TUNING[this.personality];
    const base = self.role === 'int'
      ? BOT_TUNING.FACTION_BASE_VIRGIN
      : BOT_TUNING.FACTION_BASE_CHAD;

    const hpPct = self.hp / self.hpMax;
    if (tuning.retreatPct > 0 && hpPct < tuning.retreatPct) {
      this.engagingEnemy = false;
      this.setTarget(base.x, base.z);
      return;
    }

    const enemy = nearestAlive(ctx.opponents, self.x, self.z, BOT_TUNING.ENGAGE_SIGHT);
    const enemyBoss = ctx.bosses.find((b) => b.alive && b.side !== self.role) ?? null;
    const farmNpc = nearestFarmable(ctx.npcs, self.x, self.z, BOT_TUNING.FARM_SEARCH);
    const venue = pickVenueTarget(self, ctx.elapsedMatchSec);

    // Walk the personality's priority order, collecting an action for
    // the first target-kind that's available in this tick. Unlike the
    // fixed-order table before, this lets the `farmer` prefer NPCs
    // over enemies and the `reluctant` prefer venues over combat.
    const options: Array<() => void> = [];
    for (const kind of tuning.priority) {
      if (kind === 'enemy' && enemy) {
        options.push(() => { this.engagingEnemy = true; this.setEngagePoint(self, enemy, tuning.engageIdealMult); });
      } else if (kind === 'boss' && enemyBoss && hpPct > 0.5) {
        options.push(() => { this.engagingEnemy = true; this.setEngagePoint(self, enemyBoss, tuning.engageIdealMult); });
      } else if (kind === 'npc' && farmNpc) {
        options.push(() => { this.engagingEnemy = true; this.setEngagePoint(self, farmNpc, tuning.engageIdealMult); });
      } else if (kind === 'venue' && venue) {
        options.push(() => { this.engagingEnemy = false; this.setTarget(venue.x, venue.z); });
      }
    }
    // Wander sits at the bottom as a never-null fallback — the bot is
    // never idle, just might not have a good goal.
    options.push(() => {
      this.engagingEnemy = false;
      const nearMid = (Math.random() - 0.5) * 60;
      const driftZ = (Math.random() - 0.5) * 40;
      this.setTarget(nearMid, driftZ);
    });

    const blunder = Math.random() < tuning.blunderChance;
    const idx = (blunder && options.length > 1) ? 1 : 0;
    options[idx]();
  }

  // Park the target at the bot's weapon sweet spot relative to the foe —
  // inside circle radius for Chad, in the ring band for Virgin.
  // `idealMult` comes from the personality tuning so kamikaze archetypes
  // hug closer than reluctant ones.
  private setEngagePoint(self: BotSelf, foe: { x: number; z: number }, idealMult = 1): void {
    const baseIdeal = self.role === 'ext'
      ? BOT_TUNING.CIRCLE_IDEAL
      : BOT_TUNING.RING_IDEAL;
    const ideal = Math.max(0.2, baseIdeal * idealMult);
    const fx = foe.x - self.x;
    const fz = foe.z - self.z;
    const d = Math.hypot(fx, fz);
    if (d < 0.001) {
      this.setTarget(foe.x, foe.z);
      return;
    }
    this.setTarget(foe.x - (fx / d) * ideal, foe.z - (fz / d) * ideal);
  }

  private setTarget(x: number, z: number): void {
    this.targetX = clamp(x, BOT_TUNING.MAP_BOUNDS.xMin, BOT_TUNING.MAP_BOUNDS.xMax);
    this.targetZ = clamp(z, BOT_TUNING.MAP_BOUNDS.zMin, BOT_TUNING.MAP_BOUNDS.zMax);
  }
}

// ---- Helpers ----

function nearestAlive<T extends { x: number; z: number; alive: boolean; hood?: boolean }>(
  list: ReadonlyArray<T>,
  x: number,
  z: number,
  maxDist: number,
): T | null {
  let best: T | null = null;
  let bestDist = maxDist * maxDist;
  for (const e of list) {
    if (!e.alive) continue;
    if (e.hood) continue; // can't see hooded Virgin
    const dx = e.x - x;
    const dz = e.z - z;
    const d = dx * dx + dz * dz;
    if (d < bestDist) { bestDist = d; best = e; }
  }
  return best;
}

function nearestFarmable(
  list: ReadonlyArray<BotNpc>,
  x: number,
  z: number,
  maxDist: number,
): BotNpc | null {
  let best: BotNpc | null = null;
  let bestDist = maxDist * maxDist;
  for (const n of list) {
    // Stage 0-1 NPCs give XP safely; stage 2+ turn hostile.
    if (n.stage > 1) continue;
    const dx = n.x - x;
    const dz = n.z - z;
    const d = dx * dx + dz * dz;
    if (d < bestDist) { bestDist = d; best = n; }
  }
  return best;
}

// Return the next unvisited venue for this team whose time-gate has
// elapsed. Uses `VENUES` + the bot's `questMask` (same bitfield the
// real quest tracker writes to).
function pickVenueTarget(self: BotSelf, elapsedMatchSec: number): { x: number; z: number } | null {
  const team = VENUES.filter((v) => v.role === self.role);
  const visited = popCount(self.questMask);
  const firstGate = 60;
  const secondGate = 180;
  const gate = visited === 0 ? firstGate : secondGate;
  if (elapsedMatchSec < gate) return null;
  for (let i = 0; i < team.length; i++) {
    const bit = 1 << i;
    if ((self.questMask & bit) === 0) {
      return { x: team[i].x, z: team[i].z };
    }
  }
  return null;
}

function popCount(n: number): number {
  let c = 0;
  while (n) { c += n & 1; n >>>= 1; }
  return c;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// ---- Level-up auto-pick ----

// Rarity pick bias for bots. Higher rarities get exponentially more
// weight so bots greedily (but not deterministically) favor legendary
// over common. Keeps rubber-banding — a legendary isn't always chosen
// over a crisp common synergy — while clearly biasing up the ladder.
const BOT_RARITY_BIAS: Record<Rarity, number> = {
  common: 1,
  uncommon: 3,
  rare: 9,
  epic: 27,
  legendary: 81,
};

// Pick a powerup id from the eligible pool, biased toward higher
// rarity. Used by the server + SoloStub when a bot levels up — bots
// don't open the pick modal. Rolls 2 choices (same cap as the player
// UI so bots and humans draw from the same menu shape) then picks
// one weighted by BOT_RARITY_BIAS. Returns null when no eligible
// powerups remain for this player (already picked them all).
export function botPickPowerup(role: Role, ownedOneTime: ReadonlySet<string>): string | null {
  const rolled = rollChoices(role, ownedOneTime, 2);
  if (rolled.length === 0) return null;
  if (rolled.length === 1) return rolled[0].id;
  const total = rolled.reduce((s, p) => s + BOT_RARITY_BIAS[p.rarity], 0);
  let r = Math.random() * total;
  for (const p of rolled) {
    r -= BOT_RARITY_BIAS[p.rarity];
    if (r <= 0) return p.id;
  }
  return rolled[rolled.length - 1].id;
}
